import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { FeedClient, FeedSnapshot } from './feed';
import { KEYS, local } from './storage';

export function useFeed(client: FeedClient): FeedSnapshot {
  useEffect(() => {
    client.start();
    return () => client.stop();
  }, [client]);
  return useSyncExternalStore(client.subscribe, client.getSnapshot);
}

/** Ticks once a second with the server-corrected time. `fixed` freezes it to a demo clock. */
export function useNow(offsetMs: number, demoOffsetMs: number | null): number {
  const compute = () => Date.now() + (demoOffsetMs ?? offsetMs);
  const [now, setNow] = useState(compute);
  useEffect(() => {
    setNow(compute());
    const t = setInterval(() => setNow(compute()), 1000);
    return () => clearInterval(t);
  }, [offsetMs, demoOffsetMs]);
  return now;
}

/** Favorites live in this browser only. No account, nothing sent to a server. */
export function useFavorites(): [ReadonlySet<string>, (id: string) => boolean] {
  const [ids, setIds] = useState<string[]>(() => local.json<string[]>(KEYS.favorites, []));
  const latest = useRef(ids);
  latest.current = ids;
  useEffect(() => {
    const on = (e: StorageEvent) => {
      if (e.key === KEYS.favorites) setIds(local.json<string[]>(KEYS.favorites, []));
    };
    window.addEventListener('storage', on);
    return () => window.removeEventListener('storage', on);
  }, []);
  /** Returns true when the unit was added, false when removed. */
  const toggle = useCallback((id: string): boolean => {
    const prev = latest.current;
    const added = !prev.includes(id);
    const next = added ? [...prev, id] : prev.filter((x) => x !== id);
    latest.current = next;
    local.set(KEYS.favorites, JSON.stringify(next));
    setIds(next);
    return added;
  }, []);
  const set = useMemo(() => new Set(ids), [ids]);
  return [set, toggle];
}

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select,textarea,summary,[tabindex]:not([tabindex="-1"])';

/** Focus trap and Escape-to-close for dialogs; restores focus to the opener when closed. */
export function useDialog<T extends HTMLElement>(onClose: () => void) {
  const ref = useRef<T>(null);
  const cb = useRef(onClose);
  cb.current = onClose;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    if (!el) return;
    (el.querySelector<HTMLElement>('[data-autofocus]') ?? el).focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        cb.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const fs = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (x) => x.offsetParent !== null || x === document.activeElement,
      );
      if (!fs.length) return;
      const first = fs[0]!;
      const last = fs[fs.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    el.addEventListener('keydown', onKey);
    return () => {
      el.removeEventListener('keydown', onKey);
      if (prev?.focus && document.contains(prev)) prev.focus({ preventScroll: true });
    };
  }, []);
  return ref;
}

/** Debounced callback for free-text analytics such as search terms. */
export function useDebouncedEffect(fn: () => void, deps: unknown[], ms: number) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    const t = setTimeout(() => ref.current(), ms);
    return () => clearTimeout(t);
  }, deps);
}
